import * as THREE from "three";

// ---------- math ----------
export const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
export const lerp = (a, b, t) => a + (b - a) * t;
export const falloff = (t, smooth) => (t >= 1 ? 0 : Math.pow(1 - t * t, 0.5 + smooth * 3));

// ---------- geometry / grid ----------
export function forEachInRadius(ctx, x, z, R, fn) {
  const { N, sp, half, px } = ctx;
  const c0 = Math.max(0, Math.floor((x - R + half) / sp)), c1 = Math.min(N - 1, Math.ceil((x + R + half) / sp));
  const r0 = Math.max(0, Math.floor((z - R + half) / sp)), r1 = Math.min(N - 1, Math.ceil((z + R + half) / sp));
  for (let r = r0; r <= r1; r++) {
    const dz = px[r] - z;
    for (let c = c0; c <= c1; c++) {
      const dx = px[c] - x, d = Math.sqrt(dx * dx + dz * dz);
      if (d < R) fn(r * N + c, d, dx, dz);
    }
  }
}

export function blur(src, N, passes = 1) {
  let a = src, b = new Float32Array(src.length);
  for (let p = 0; p < passes; p++) {
    for (let r = 0; r < N; r++) {
      const r0 = Math.max(0, r - 1) * N, r1 = r * N, r2 = Math.min(N - 1, r + 1) * N;
      for (let c = 0; c < N; c++) {
        const c0 = Math.max(0, c - 1), c2 = Math.min(N - 1, c + 1);
        b[r1 + c] = (a[r0 + c0] + a[r0 + c] + a[r0 + c2] + a[r1 + c0] + a[r1 + c] * 2 + a[r1 + c2] + a[r2 + c0] + a[r2 + c] + a[r2 + c2]) / 10;
      }
    }
    [a, b] = [b, a];
  }
  return a;
}

export function resample(src, oN, N) {
  const out = new Float32Array(N * N);
  for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
    const fx = (c / (N - 1)) * (oN - 1), fy = (r / (N - 1)) * (oN - 1);
    const x0 = Math.floor(fx), y0 = Math.floor(fy), x1 = Math.min(oN - 1, x0 + 1), y1 = Math.min(oN - 1, y0 + 1);
    const tx = fx - x0, ty = fy - y0;
    out[r * N + c] = lerp(lerp(src[y0 * oN + x0], src[y0 * oN + x1], tx), lerp(src[y1 * oN + x0], src[y1 * oN + x1], tx), ty);
  }
  return out;
}

export const laplacian = (a, N, i, r, c) =>
  a[c > 0 ? i - 1 : i] + a[c < N - 1 ? i + 1 : i] + a[r > 0 ? i - N : i] + a[r < N - 1 ? i + N : i] - 4 * a[i];

// ---------- force calculations (shared by magnetic + gravity) ----------
export function inverseSquare(sources, x, z, soft) {
  let fx = 0, fz = 0, pot = 0;
  for (let k = 0; k < sources.length; k++) {
    const s = sources[k], dx = x - s.x, dz = z - s.z;
    const r2 = dx * dx + dz * dz + soft * soft, r = Math.sqrt(r2), inv = s.q / (r2 * r);
    fx += dx * inv; fz += dz * inv; pot += s.q / r;
  }
  return [fx, fz, pot];
}

// ---------- image sampling ----------
const cv = document.createElement("canvas");
const c2d = cv.getContext("2d", { willReadFrequently: true });
export function sampleSource(src, N, mirror = false) {
  const w = src.videoWidth || src.naturalWidth || src.width, h = src.videoHeight || src.naturalHeight || src.height;
  if (!w || !h) return null;
  cv.width = N; cv.height = N;
  const s = Math.min(w, h);
  c2d.save();
  if (mirror) { c2d.translate(N, 0); c2d.scale(-1, 1); }
  c2d.drawImage(src, (w - s) / 2, (h - s) / 2, s, s, 0, 0, N, N);
  c2d.restore();
  const d = c2d.getImageData(0, 0, N, N).data, lum = new Float32Array(N * N), rgb = new Float32Array(N * N * 3);
  for (let i = 0; i < N * N; i++) {
    const r = d[i * 4] / 255, g = d[i * 4 + 1] / 255, b = d[i * 4 + 2] / 255;
    rgb[i * 3] = r; rgb[i * 3 + 1] = g; rgb[i * 3 + 2] = b;
    lum[i] = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }
  return { lum, rgb };
}

export function levels(l, black, white, gamma, invert) {
  const t = Math.pow(clamp((l - black) / Math.max(0.001, white - black), 0, 1), gamma);
  return invert ? 1 - t : t;
}

export function sobel(l, N) {
  const out = new Float32Array(N * N);
  for (let r = 1; r < N - 1; r++) for (let c = 1; c < N - 1; c++) {
    const i = r * N + c;
    const gx = l[i - N + 1] + 2 * l[i + 1] + l[i + N + 1] - l[i - N - 1] - 2 * l[i - 1] - l[i + N - 1];
    const gy = l[i + N - 1] + 2 * l[i + N] + l[i + N + 1] - l[i - N - 1] - 2 * l[i - N] - l[i - N + 1];
    out[i] = Math.min(1, Math.sqrt(gx * gx + gy * gy) * 1.5);
  }
  return out;
}

export function requestMedia(constraints, what = "Microphone") {
  if (!navigator.mediaDevices?.getUserMedia) return Promise.reject(new Error(`${what} not available in this browser`));
  const timeout = new Promise((_, rej) => setTimeout(() => rej(new Error(`${what} did not respond — check browser permissions`)), 10000));
  return Promise.race([navigator.mediaDevices.getUserMedia(constraints), timeout]).catch((e) => {
    throw new Error(e.message?.includes("respond") ? e.message : `${what} access was blocked`);
  });
}


export function loadImage(file) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = () => rej(new Error("Could not read that image"));
    img.src = URL.createObjectURL(file);
  });
}

// ---------- material / color system ----------
const POS = [0, 0.94, 1], NEG = [1, 0.62, 0.05];
export function tint(colors, i, t, amt = 0.85) {
  const a = Math.min(1, Math.abs(t)) * amt, c = t >= 0 ? POS : NEG;
  colors[i * 3] = 1 + (c[0] - 1) * a; colors[i * 3 + 1] = 1 + (c[1] - 1) * a; colors[i * 3 + 2] = 1 + (c[2] - 1) * a;
}
export const PALETTE = [[0.42, 0.3, 1], [0, 0.94, 1], [1, 1, 1], [1, 0.72, 0.1]];
export function ramp(colors, i, t, stops = PALETTE) {
  const x = clamp(t, 0, 1) * (stops.length - 1), k = Math.min(stops.length - 2, Math.floor(x)), f = x - k;
  const a = stops[k], b = stops[k + 1];
  colors[i * 3] = lerp(a[0], b[0], f); colors[i * 3 + 1] = lerp(a[1], b[1], f); colors[i * 3 + 2] = lerp(a[2], b[2], f);
}

// ---------- rendering infrastructure ----------
export class LineBuffer {
  constructor(overlay, maxSegs, opacity = 0.85) {
    this.max = maxSegs;
    this.pos = new Float32Array(maxSegs * 6); this.col = new Float32Array(maxSegs * 6);
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute("color", new THREE.BufferAttribute(this.col, 3).setUsage(THREE.DynamicDrawUsage));
    this.obj = new THREE.LineSegments(g, new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity, blending: THREE.AdditiveBlending, depthWrite: false }));
    this.obj.frustumCulled = false;
    overlay.add(this.obj); this.overlay = overlay; this.n = 0;
  }
  begin() { this.n = 0; }
  seg(x1, y1, z1, x2, y2, z2, r, g, b, r2 = r, g2 = g, b2 = b) {
    if (this.n >= this.max) return;
    const o = this.n * 6;
    this.pos.set([x1, y1, z1, x2, y2, z2], o); this.col.set([r, g, b, r2, g2, b2], o); this.n++;
  }
  end() {
    const g = this.obj.geometry; g.setDrawRange(0, this.n * 2);
    g.attributes.position.needsUpdate = true; g.attributes.color.needsUpdate = true;
  }
  dispose() { this.overlay.remove(this.obj); this.obj.geometry.dispose(); this.obj.material.dispose(); }
}

export class Particles {
  constructor(overlay, count, color, size = 0.06) {
    this.count = count; this.pos = new Float32Array(count * 3); this.vel = new Float32Array(count * 2); this.age = new Float32Array(count);
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.obj = new THREE.Points(g, new THREE.PointsMaterial({ color, size, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, depthWrite: false }));
    this.obj.frustumCulled = false; overlay.add(this.obj); this.overlay = overlay;
  }
  set visible(v) { this.obj.visible = v; }
  update() { this.obj.geometry.attributes.position.needsUpdate = true; }
  dispose() { this.overlay.remove(this.obj); this.obj.geometry.dispose(); this.obj.material.dispose(); }
}

const orbGeo = new THREE.SphereGeometry(1, 24, 16);
export function makeOrb(overlay, color, radius) {
  const m = new THREE.Mesh(orbGeo, new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 1.3, metalness: 0.3, roughness: 0.25 }));
  m.scale.setScalar(radius); m.castShadow = true; overlay.add(m);
  return m;
}
export function removeObj(overlay, m) { overlay.remove(m); m.material?.dispose(); }
